import closeIcon from '@assets/images/close_icon.png';
import logo from '@assets/svg/logo_invert.svg';
import { DEFAULT_ANIMATION_DURATION, SIDEBAR_LINKS } from '@common/constants';
import { Avatar, Button } from '@cloudclevr/components';
import { LogoutIcon } from '@cloudclevr/components';
import useAuthStore from '@store/authStore';
import useUIStore from '@store/uiStore';
import React, { useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Transition } from 'react-transition-group';
import './styles.css';
import { useCurrentUserQuery } from '@hooks/users';
import { useIsDesktop, useIsTablet } from '@hooks/utils';
import { TransitionState } from '@components/ui/Overlay';
import { hasPermission } from '@utils/index';
import { UserRoles, Permissions } from '@hooks/users/types';
import { useFeatureFlags } from '@hooks/utils/dashboard/features/useFeatureFlags';
import { TECH_ROADMAPS_VIEW_FLAG } from '@common/constants';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@auth';


export const Sidebar: React.FC = () => {
  const authContext = useAuth();
  const authStore = useAuthStore();
  const nodeRef = useRef(null);
  const { setSidebar } = useUIStore();
  const location = useLocation();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();
  const userData = useCurrentUserQuery();

  // Get feature flags
  const featureFlags = useFeatureFlags() as Record<string, boolean>;
  const [, setIsClosedClicked] = useState(false);
  const isSidebarOpen = useUIStore((state) => state.isSidebarOpen);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);

  const userRole = useAuthStore.getState().role || UserRoles.STANDARD;
  const canAccessTechnologyRoadmaps = hasPermission(
    userRole,
    Permissions.ADMINS_SUPPORTUSER
  );
  const navigate = useNavigate();

  const handleSidebarClose = () => {
    setIsClosedClicked(true);
    toggleSidebar();
  };

  const handleLinkClick = () => {
    setSidebar(false);
  };

  const isLinkActive = (to: string) => {
    const { pathname } = location;
    const nestedRoutes = ['accounts', 'insights-and-analytics', 'settings', 'roadmaps'];
    let isActive = false;

    if (pathname === to) {
      isActive = true;
    } else {
      nestedRoutes.forEach((route) => {
        if (pathname.includes(route) && to.includes(route)) {
          isActive = true;
        }
      });
    }

    const styles = {
      active:
        'block border-b-2 border-primaryBrandColor-100 pb-1 pl-0 pr-0 pt-1 text-interfaceColor-20 transition-colors duration-100 hover:cursor-pointer md:pb-3 md:pl-0 md:pr-0 md:pt-3',
      inactive:
        'block border-b-2 border-transparent pb-1 pl-0 pr-0 pt-1 text-interfaceColor-40 transition-colors duration-100 hover:cursor-pointer hover:border-interfaceColor-20 hover:text-interfaceColor-20 md:pb-3 md:pl-0 md:pr-0 md:pt-3',
    };

    return isActive ? styles.active : styles.inactive;
  };

  const handleLogout = async () => {
    authStore.setIsAuthenticated(false);
    authStore.setToken(null);
    authStore.setUserRole(null);
    authStore.setHasSelectedProfile(null);

    try {
      await authContext.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const defaultStyle = {
    transition: `transform 300ms ease-in-out`,
    transform: 'translateX(101%)',
  };

  const transitionStyles: Record<TransitionState, React.CSSProperties> = {
    entering: { transform: 'translateX(101%)' },
    entered: { transform: 'translateX(0)' },
    exiting: { transform: 'translateX(101%)' },
    exited: { transform: 'translateX(101%)' },
    unmounted: {},
  };
  const orgToken = sessionStorage.getItem("organizationToken");

    // Filter sidebar links based on feature flag for technology roadmaps
  const filteredSidebarLinks = SIDEBAR_LINKS.filter(link => {
    if (link.featureFlag) {    
      return featureFlags[link.featureFlag] === true;    
    }
    return true;
  });

  return (
    <>
      {isDesktop && !isTablet ? (
        <div className="lg:p-4">
          <nav
              ref={nodeRef}
              className={`fixed left-[16px] top-[16px] z-30 flex h-screen flex-col bg-interfaceColor-100 p-0 transition-transform duration-300 ease-out md:h-[calc(100vh-32px)] md:w-[312px] md:min-w-[280px] lg:w-[276px] lg:rounded`}
            >
            <div className="flex h-[76px] justify-between p-5 md:h-[92px] md:p-6 lg:pb-8 lg:pl-6 lg:pr-6 lg:pt-8">
              <img
                className="w-[158px] gap-[8px] lg:w-[151px]"
                src={logo}
                alt="CloudClevr Logo"
              />
              <div className="h-[36px] w-[36px] lg:hidden">
                <button onClick={handleSidebarClose}>
                  <img src={closeIcon} alt="Close Sidebar" />
                </button>
              </div>
            </div>

            <div className="pb-5 pl-5 pr-5 pt-0 md:pb-6 md:pl-6 md:pr-6 md:pt-0">
              <ul className="flex flex-col items-start text-20 leading-7 text-interfaceColor-40">
                {filteredSidebarLinks.filter(({ to }) => {
                      if (to === '/roadmaps' && !canAccessTechnologyRoadmaps) {
                        return false;
                      }
                      return true;
                    }).map(({ to, label }) => (
                  <li className="mt-2 md:w-full lg:w-[212px]" key={to}>
                    <NavLink
                      onClick={handleLinkClick}
                      to={to}
                      className={isLinkActive(to)}
                    >
                      {label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-auto h-[96px] p-6">
              <div className="flex gap-[8px]">
                <Avatar 
                  fullName={userData?.data?.fullName} 
                  onClick={() => navigate('/my-profile/details')} 
                />
                <div className="flex flex-col justify-start">
                    {!orgToken ? (
                    <Link
                      to="/my-profile/details"
                      className="font-semibold text-interfaceColor-40"
                    >
                      {userData?.data?.fullName}
                    </Link>
                    ) : (
                    <p className="font-semibold text-interfaceColor-40">
                      {userData?.data?.fullName}
                    </p>
                    )}
                  <Button
                    testId='handle-logout-button'
                    className="ml-0 justify-start self-start !p-0 text-interfaceColor-40 !shadow-none"
                    variant={'white'}
                    onClick={handleLogout}
                    type="button"
                    size="extraSmall"
                  >
                    <div className="flex">
                      <LogoutIcon classNames="h-2 w-2" />
                      <span className="leading-3">Log out</span>
                    </div>
                  </Button>
                </div>
              </div>
            </div>
          </nav>
        </div>
      ) : (
        <Transition
          in={isSidebarOpen}
          timeout={DEFAULT_ANIMATION_DURATION}
          unmountOnExit
          classNames={{
            enter: 'sidebar-enter',
            enterActive: 'sidebar-enter-active',
            exit: 'sidebar-exit',
            exitActive: 'sidebar-exit-active',
          }}
          nodeRef={nodeRef}
          appear
        >
          {(state) => (
            <nav
              style={{
                ...defaultStyle,
                ...transitionStyles[state],
              }}
              ref={nodeRef}
              className={`fixed z-30 flex flex-col bg-interfaceColor-100 p-0 ${
                isTablet
                  ? 'right-0 top-0 h-screen w-[312px]'
                  : 'left-[16px] top-[16px] md:h-[calc(100vh-32px)] md:w-[312px] md:min-w-[280px] lg:w-[276px] lg:rounded'
              } ${
                isTablet && isSidebarOpen
                  ? 'translate-x-0 transform'
                  : 'transform'
              } transition-transform duration-300 ease-out lg:rounded`}
            >
              <div className="flex h-[76px] justify-between p-5 md:h-[92px] md:p-6 lg:pl-6 lg:pr-6 lg:pt-8">
                <img
                  className="w-[158px] gap-[8px] lg:w-[151px]"
                  src={logo}
                  alt="CloudClevr Logo"
                />
                <div className="h-[36px] w-[36px]">
                  <button onClick={handleSidebarClose}>
                    <img src={closeIcon} alt="Close Sidebar" />
                  </button>
                </div>
              </div>

              <div className="pb-5 pl-5 pr-5 pt-0 md:pb-6 md:pl-6 md:pr-6 md:pt-0">
                <ul className="flex flex-col items-start text-20 leading-7 text-interfaceColor-40">
                  {filteredSidebarLinks.map(({ to, label }) => (
                    <li className="mt-2 md:w-full lg:w-[212px]" key={to}>
                      <NavLink
                        onClick={handleLinkClick}
                        to={to}
                        className={isLinkActive(to)}
                      >
                        {label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>

              {/* ToDo: Roadmap link */}
              {/* 
        <div className="flex w-full flex-col bg-interfaceColor-90 px-[24px] py-[20px]">
          <span className="block text-20 text-primaryBrandColor-100">
            See the future of CloudClevr
          </span>
          <span className="block text-16 text-interfaceColor-15">
            Check out our open roadmap and add your ideas
          </span>
          <Button
            variant="default"
            type="button"
            className="justify-between text-14"
          >
            <div className="flex ">
              <span className="text-18">See the roadmap</span>
              <ArrowRightIcon
                color={'text-interfaceColor-100'}
                size="3"
                classNames="ml-2 mt-[2px]"
              />
            </div>
          </Button>
        </div> */}

              <div className="mt-auto h-[96px] p-6">
                <div className="flex gap-[8px]">
                  <Avatar 
                  fullName={userData?.data?.fullName} 
                  onClick={() => navigate('/my-profile/details')} 
                  />
                  <div>
                    <p className="font-semibold text-interfaceColor-40">
                      {userData?.data?.fullName}
                    </p>
                    <Button
                      testId='logout-button'
                      className="!p-0 text-interfaceColor-40 !shadow-none"
                      variant={'white'}
                      onClick={handleLogout}
                      type="button"
                      size="extraSmall"
                    >
                      <div className="flex">
                        <LogoutIcon classNames="h-2 w-2" />
                        <span className="leading-3">Log out</span>
                      </div>
                    </Button>
                  </div>
                </div>
              </div>
            </nav>
          )}
        </Transition>
      )}
    </>
  );
};
